/**
 * Ferramentas (painel GM) — ações globais não ligadas a um char/conta.
 * Tabs no estilo do Guard. Por enquanto uma única ferramenta: Procurar Item.
 *
 * As antigas abas foram redistribuídas:
 *   - IPs Bloqueados  -> removida (legado; todo banimento é pelo Guard)
 *   - Guilds          -> menu próprio /admin/guilds
 *   - Mail em Massa   -> modal "Enviar Email" no menu /admin/emails
 *   - Criar Conta     -> modal "Criar conta" no menu /admin/contas
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Wrench, Search, Loader2, AlertTriangle, PackageSearch, Package, Warehouse,
  Bike, Gavel, Users2,
} from 'lucide-react';
import { useToast } from '@/shared/contexts/ToastContext';
import { DataTable } from '@/shared/components/ui/DataTable';
import { useTableSort } from '@/shared/hooks/useTableSort';
import {
  searchItem, type ItemSearchResponse, type ItemSearchOwner,
} from '@/shared/services/adminService';

type ToolTab = 'item-search';

export default function GmToolsPage() {
  const [tab, setTab] = useState<ToolTab>('item-search');
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
          <Wrench className="text-[#00e1d7]" size={26} /> Ferramentas
        </h1>
        <p className="text-sm text-white/65 mt-1">
          Ações globais do painel — não ligadas a um personagem/conta específico.
        </p>
      </div>

      {/* Tabs (mesmo padrão do Guard) */}
      <div className="flex flex-wrap gap-1 mb-5 surface-card rounded-xl p-1 w-fit max-w-full">
        <TabBtn active={tab === 'item-search'} onClick={() => setTab('item-search')}>
          <PackageSearch size={14} /> Procurar Item
        </TabBtn>
      </div>

      {tab === 'item-search' && <ItemSearchTab />}
    </div>
  );
}

function TabBtn({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer whitespace-nowrap ${
        active
          ? 'bg-[#00e1d7]/15 text-[#00e1d7]'
          : 'text-white/55 hover:text-white hover:bg-white/[0.03]'
      }`}
    >
      {children}
    </button>
  );
}

/* ───────────────────────────────────────────── Procurar Item ──── */

type Scope = 'both' | 'inv' | 'wh';

function LocIcon({ r }: { r: ItemSearchOwner }) {
  if (r.ownerType === 'guild') return <Users2 size={13} className="text-purple-400" />;
  if (r.ownerType === 'auction') return <Gavel size={13} className="text-yellow-400" />;
  if (r.location === 'Bike') return <Bike size={13} className="text-sky-400" />;
  if (r.ownerType === 'user') return <Warehouse size={13} className="text-amber-400" />;
  return <Package size={13} className="text-[#00e1d7]" />;
}

function ItemSearchTab() {
  const { notify } = useToast();
  const [itemId, setItemId] = useState('');
  const [scope, setScope] = useState<Scope>('both');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ItemSearchResponse | null>(null);

  const run = async () => {
    const id = parseInt(itemId.trim(), 10);
    if (!id || id < 1) { notify('error', 'Informe um Item ID válido'); return; }
    setLoading(true);
    setResult(null);
    try {
      let res: ItemSearchResponse;
      if (scope === 'both') {
        // "Ambos" = 2 chamadas paralelas (inv | baú+leilão). Cada uma fica bem
        // abaixo do timeout do proxy (60s); o scan de inventário sozinho ~55s.
        const [inv, wh] = await Promise.all([searchItem(id, 'inv'), searchItem(id, 'wh')]);
        res = {
          ...inv,
          scope: 'both',
          itemName: inv.itemName || wh.itemName,
          owners: [...inv.owners, ...wh.owners],
          ownerCount: inv.ownerCount + wh.ownerCount,
          totalQty: inv.totalQty + wh.totalQty,
        };
      } else {
        res = await searchItem(id, scope);
      }
      setResult(res);
      if (res.ownerCount === 0) notify('info', 'Nenhum jogador possui esse item');
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro na busca');
    } finally {
      setLoading(false);
    }
  };

  const { sorted, sort, toggle } = useTableSort(result?.owners ?? [], { key: 'qty', order: 'desc' });

  return (
    <div>
      {/* Aviso de query pesada */}
      <div className="surface-card rounded-xl p-3 mb-4 flex items-start gap-2 border-amber-500/25 bg-amber-500/[0.04]">
        <AlertTriangle size={15} className="text-amber-400 shrink-0 mt-0.5" />
        <p className="text-[11px] text-white/65 leading-relaxed">
          Busca <b>pesada</b>: varre o servidor slot a slot. <b>Inventário</b> = inventário +
          equipado + bike. <b>Baú</b> = baú da conta + baú de guild. <b>Ambos</b> inclui também
          o <b>leilão</b> (agent shop). Selecionar um lado só deixa mais rápido. Encontra o item
          com qualquer +upgrade/bind.
        </p>
      </div>

      {/* Form */}
      <div className="surface-card rounded-xl p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px] relative">
            <label className="text-[11px] text-white/55 mb-1 block">Item ID</label>
            <Search size={14} className="absolute left-3 bottom-2.5 text-white/35" />
            <input
              type="text" inputMode="numeric" value={itemId}
              onChange={(e) => setItemId(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
              placeholder="ex: 2364"
              className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg pl-9 pr-3 py-2 text-sm text-white font-mono"
            />
          </div>
          <div>
            <label className="text-[11px] text-white/55 mb-1 block">Onde buscar</label>
            <div className="flex gap-1.5">
              {([['both', 'Ambos'], ['inv', 'Inventário'], ['wh', 'Baú']] as const).map(([val, lbl]) => (
                <button
                  key={val} type="button" onClick={() => setScope(val)}
                  className={`px-3 py-2 rounded-lg text-xs font-bold cursor-pointer transition border
                    ${scope === val
                      ? 'bg-[#00e1d7]/15 text-[#00e1d7] border-[#00e1d7]/40'
                      : 'bg-white/[0.03] text-white/60 border-white/[0.06] hover:text-white'}`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={run} disabled={loading}
            className="px-5 py-2 rounded-lg bg-[#00e1d7]/15 border border-[#00e1d7]/30 text-[#00e1d7] font-semibold text-sm hover:bg-[#00e1d7]/25 transition cursor-pointer disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            {loading ? 'Buscando...' : 'Buscar'}
          </button>
        </div>
      </div>

      {/* Resultado */}
      {result && (
        <>
          <div className="surface-card rounded-xl p-4 mb-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/45 font-bold">Item</p>
                <p className="text-sm text-white font-semibold">
                  {result.itemName || 'Nome não encontrado'}
                  <span className="text-white/40 font-mono ml-2">#{result.itemId}</span>
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/45 font-bold">Donos</p>
                <p className="text-lg text-white font-black leading-none">{result.ownerCount}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/45 font-bold">Qtd total</p>
                <p className="text-lg text-white font-black leading-none">{result.totalQty.toLocaleString('pt-BR')}</p>
              </div>
            </div>
          </div>

          <DataTable<ItemSearchOwner>
            columns={[
              { key: 'location', label: 'Local', width: '130px', sortKey: 'location',
                cell: (r) => (
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/80">
                    <LocIcon r={r} /> {r.location}
                  </span>
                ) },
              { key: 'account', label: 'Conta', width: '1.4fr', sortKey: 'accountId',
                cell: (r) => r.ownerType === 'guild' ? (
                  <span className="inline-flex items-center gap-1.5 text-sm text-white/85">
                    <Users2 size={13} className="text-purple-400" />
                    {r.guildName || `Guild ${r.guildNo}`}
                    <span className="text-[10px] text-white/45 font-mono">#{r.guildNo}</span>
                  </span>
                ) : (
                  <Link to={`/admin/account/${r.userNum}`} onClick={(e) => e.stopPropagation()}
                        className="min-w-0 hover:text-[#00e1d7] transition inline-block">
                    <span className="text-sm font-semibold text-white truncate">{r.accountId || `User ${r.userNum}`}</span>
                    <span className="block text-[10px] text-white/45 font-mono">UserNum {r.userNum}</span>
                  </Link>
                ) },
              { key: 'char', label: 'Personagem', width: '1.4fr', sortKey: 'charName',
                cell: (r) => r.charIdx ? (
                  <Link to={`/admin/character/${r.charIdx}`} onClick={(e) => e.stopPropagation()}
                        className="min-w-0 hover:text-[#00e1d7] transition inline-block">
                    <span className="text-sm text-white truncate">{r.charName || `Char ${r.charIdx}`}</span>
                    <span className="block text-[10px] text-white/45 font-mono">CharIdx {r.charIdx}</span>
                  </Link>
                ) : r.ownerType === 'auction' ? (
                  <span className="text-xs text-white/70 truncate">{r.charName || '—'} <span className="text-white/35">(vendedor)</span></span>
                ) : r.ownerType === 'guild' ? (
                  <span className="text-xs italic text-white/35">— (baú de guild)</span>
                ) : (
                  <span className="text-xs italic text-white/35">— (baú da conta)</span>
                ) },
              { key: 'option', label: 'Option', width: '110px', align: 'right', sortKey: 'option',
                cell: (r) => r.option ? (
                  <span className="inline-flex items-center gap-1 text-xs font-mono text-[#00e1d7]/85"
                        title={r.optionVaries ? 'Cópias com options diferentes — mostrando a maior' : undefined}>
                    {r.option}
                    {r.optionVaries && <span className="text-white/40 not-italic">+</span>}
                  </span>
                ) : <span className="text-white/25">—</span> },
              { key: 'qty', label: 'Qtd', width: '80px', align: 'right', sortKey: 'qty',
                cell: (r) => <span className="text-sm font-bold text-white">{r.qty}</span> },
            ]}
            rows={sorted}
            sort={sort}
            onSort={toggle}
            rowKey={(r) => `${r.src}-${r.guildNo ?? r.charIdx ?? r.userNum}`}
            loading={loading}
            empty="Nenhum jogador possui esse item"
            mobileTitle={(r) => r.ownerType === 'guild' ? (r.guildName || `Guild ${r.guildNo}`) : (r.accountId || `User ${r.userNum}`)}
            mobileSubtitle={(r) => `${r.location} · ${r.charName || r.guildName || 'baú'} · ${r.qty}x`}
          />
        </>
      )}
    </div>
  );
}
